/**
 * The video-call settings' validation (Settings → General → Video calls),
 * apart from the rest of `videoCall.ts` (#577 follow-up): the mock adapter
 * validates with these, and importing them from `videoCall.ts` kept the whole
 * meeting-link classifier on every first paint although only lazy calendar
 * and settings chunks use it.
 */

/** The server refuses a longer URL; the form says so first. */
export const VIDEO_CALL_URL_MAX = 200;

/**
 * The server's own sentence for a port other than 443
 * (`store.ErrVideoCallPort`), said here first so the form never offers to
 * save or test an address the server will refuse.
 */
export const VIDEO_CALL_PORT_PROBLEM =
  'Jitsi servers are supported on the standard HTTPS port (443) only';

/** The shape the server enforces on the prefix. */
const ROOM_PREFIX = /^[a-z0-9-]{0,24}$/;

/**
 * What is wrong with a typed Jitsi server URL, in words for a person, or
 * `undefined` when nothing is. `''` is fine: it turns video calls off.
 *
 * A first opinion only; the server is authoritative and its refusal is shown
 * verbatim. This exists so an `http://` is flagged while it is being typed
 * rather than after a round trip.
 */
export function videoServerProblem(raw: string): string | undefined {
  const value = raw.trim();
  if (value === '') return undefined;
  if (value.length > VIDEO_CALL_URL_MAX) {
    return `The address is longer than ${VIDEO_CALL_URL_MAX} characters.`;
  }
  if (!/^https:\/\//i.test(value)) {
    return 'The address has to start with https:// — a meeting link must not travel unencrypted.';
  }
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return 'That is not a web address.';
  }
  if (url.hostname === '') return 'The address has no server name.';
  if (url.username !== '' || url.password !== '') {
    return 'The address must not contain a user name or password.';
  }
  if (url.search !== '' || value.includes('?')) return 'The address must not contain a “?” query.';
  if (url.hash !== '' || value.includes('#')) return 'The address must not contain a “#” fragment.';
  // Port 443 only (ADR 0070, owner decision 2026-09-23). The parser drops a
  // written-out :443 as the https default, so any port left is another one.
  if (url.port !== '') return `${VIDEO_CALL_PORT_PROBLEM}.`;
  return undefined;
}

/** What is wrong with a typed room prefix, or `undefined`. */
export function roomPrefixProblem(raw: string): string | undefined {
  if (raw === '') return undefined;
  if (!ROOM_PREFIX.test(raw)) {
    return 'Use up to 24 lowercase letters, digits and hyphens.';
  }
  if (raw.startsWith('-') || raw.endsWith('-')) {
    return 'The prefix cannot start or end with a hyphen.';
  }
  return undefined;
}
